import { useMemo, useRef } from "react";

import { DatePicker } from "antd";
import moment from "moment";
import type { Moment } from "moment";
import type { RangeValue } from "rc-picker/lib/interface";
import styled from "styled-components";

import { useGroupByStore } from "./GroupByStoreProvider";
import { selectSelectedEndDate, selectSelectedStartDate } from "./groupBySelectors";

const { RangePicker } = DatePicker;

let nextId = 0;

interface DateRangeSelectProps {
  showLabel?: boolean;
  isFocused?: boolean;
  onSelectRef?: (index: number, selectEl: Maybe<HTMLElement>) => void;
  componentIndex?: number;
}

export default function DateRangeSelect({
  showLabel = true,
  onSelectRef,
  componentIndex = 0
}: DateRangeSelectProps) {
  const inputId = useMemo(() => `date-range-select-${nextId++}`, []);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const selectedStartDate = useGroupByStore(selectSelectedStartDate);
  const selectedEndDate = useGroupByStore(selectSelectedEndDate);
  const setSelectedStartDate = useGroupByStore((s) => s.setSelectedStartDate);
  const setSelectedEndDate = useGroupByStore((s) => s.setSelectedEndDate);

  const value: RangeValue<Moment> =
    selectedStartDate && selectedEndDate
      ? [moment(selectedStartDate), moment(selectedEndDate)]
      : null;

  function handleChange(dates: RangeValue<Moment>) {
    if (dates && dates[0] && dates[1]) {
      setSelectedStartDate(dates[0].toDate());
      setSelectedEndDate(dates[1].toDate());
    } else {
      setSelectedStartDate(null);
      setSelectedEndDate(null);
    }
  }

  function handleWrapperRef(el: HTMLDivElement) {
    (wrapperRef as React.MutableRefObject<HTMLDivElement>).current = el;
    if (onSelectRef) {
      onSelectRef(componentIndex, el);
    }
  }

  return (
    <Wrapper ref={handleWrapperRef}>
      {showLabel && <label htmlFor={inputId}>Date Range</label>}
      <StyledRangePicker
        id={inputId}
        value={value}
        onChange={handleChange}
        allowClear
        format="YYYY-MM-DD"
        placeholder={["Start date", "End date"]}
      />
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledRangePicker = styled(RangePicker)`
  width: 100%;
  min-height: 30px;
`;
